
import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";
import { useUserRoles } from "@/hooks/useUserRoles";
import { GuardWorkingHoursData } from "@/pages/scheduling/types";
import { useGuardHourlyCost } from "@/hooks/useGuardHourlyCost";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useIsMobile } from "@/hooks/use-mobile";
import { Euro, Calculator, Clock, Moon, Calendar, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface GuardCostsTableProps {
  guardsData: GuardWorkingHoursData[];
}

// Local type for computed costs per guard
type Costs = { regular: number; night: number; holiday: number; total: number };

// Row component: safe place to use hooks per guard
const GuardCostRow: React.FC<{
  guard: GuardWorkingHoursData;
  index: number;
  onComputed: (guardId: string, costs: Costs) => void;
}> = ({ guard, index, onComputed }) => {
  const regularRate = useGuardHourlyCost(guard.guardId);
  const nightRate = useGuardHourlyCost(guard.guardId, 'night');
  const holidayRate = useGuardHourlyCost(guard.guardId, 'holiday');

  const regularCost = (regularRate.hourlyCost || 0) * (guard.hours || 0);
  const nightCost = (nightRate.hourlyCost || 0) * (guard.nightHours || 0);
  const holidayCost = (holidayRate.hourlyCost || 0) * (guard.holidayHours || 0);
  const totalCost = regularCost + nightCost + holidayCost;

  useEffect(() => {
    onComputed(guard.guardId, { regular: regularCost, night: nightCost, holiday: holidayCost, total: totalCost });
  }, [guard.guardId, regularCost, nightCost, holidayCost, totalCost, onComputed]);

  return (
    <TableRow 
      key={guard.guardId}
      className="hover:bg-muted/20 transition-all duration-200 group border-b border-border/30"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <TableCell className="font-medium py-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 flex items-center justify-center border border-emerald-500/20">
            <Euro className="h-4 w-4 text-emerald-600" />
          </div>
          <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
            {guard.name}
          </span>
        </div>
      </TableCell>
      <TableCell className="text-center py-4">
        <div className="flex flex-col items-center gap-1">
          <span className="text-sm font-semibold text-foreground bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-950/20 dark:to-emerald-900/20 px-3 py-1.5 rounded-full border border-emerald-200 dark:border-emerald-800">
            €{regularCost.toFixed(2)}
          </span>
          <span className="text-xs text-muted-foreground">{guard.hours}h</span>
        </div>
      </TableCell>
      <TableCell className="text-center py-4">
        <div className="flex flex-col items-center gap-1">
          <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/20 px-3 py-1.5 rounded-full border border-indigo-200 dark:border-indigo-800">
            €{nightCost.toFixed(2)}
          </span>
          <span className="text-xs text-muted-foreground">{guard.nightHours}h</span>
        </div>
      </TableCell>
      <TableCell className="text-center py-4">
        <div className="flex flex-col items-center gap-1">
          <span className="text-sm font-semibold text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/20 px-3 py-1.5 rounded-full border border-orange-200 dark:border-orange-800">
            €{holidayCost.toFixed(2)}
          </span>
          <span className="text-xs text-muted-foreground">{guard.holidayHours}h</span>
        </div>
      </TableCell>
      <TableCell className="text-center py-4">
        <div className="flex flex-col items-center gap-1">
          <span className="text-lg font-bold text-foreground bg-gradient-to-r from-primary/20 to-primary/10 px-4 py-2 rounded-full border-2 border-primary/30 shadow-sm">
            €{totalCost.toFixed(2)}
          </span>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <TrendingUp className="h-3 w-3" />
            <span>Total</span>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
};

const GuardCostsTable: React.FC<GuardCostsTableProps> = ({ guardsData }) => {
  const { t } = useLanguage();
  const { isAdmin } = useUserRoles();
  const isMobile = useIsMobile();

  // Aggregate costs reported from rows
  const [costsByGuard, setCostsByGuard] = useState<Record<string, Costs>>({});
  const onRowComputed = useCallback((guardId: string, costs: Costs) => {
    setCostsByGuard(prev => {
      const prevCosts = prev[guardId];
      if (
        prevCosts &&
        prevCosts.regular === costs.regular &&
        prevCosts.night === costs.night &&
        prevCosts.holiday === costs.holiday &&
        prevCosts.total === costs.total
      ) {
        return prev; // no change
      }
      return { ...prev, [guardId]: costs };
    });
  }, []);

  const totalCosts = useMemo(() => {
    const totals = Object.values(costsByGuard).reduce(
      (acc, c) => ({
        regular: acc.regular + c.regular,
        night: acc.night + c.night,
        holiday: acc.holiday + c.holiday,
        total: acc.total + c.total,
      }),
      { regular: 0, night: 0, holiday: 0, total: 0 }
    );
    return totals;
  }, [costsByGuard]);

  if (!isAdmin) return null;

  return (
    <div className="mt-8 w-full overflow-hidden animate-fade-in">
      <Card className="bg-gradient-to-br from-emerald-50/50 to-emerald-100/30 dark:from-emerald-950/20 dark:to-emerald-900/10 border-emerald-200/50 dark:border-emerald-800/30 shadow-lg">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-3 text-xl font-bold text-foreground">
            <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-md">
              <Calculator className="h-6 w-6 text-white" />
            </div>
            <span>{t("costBreakdown") || "Cost Breakdown"}</span>
          </CardTitle>
          
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <div className="bg-white/80 dark:bg-background/80 rounded-lg p-3 border border-emerald-200/50 dark:border-emerald-800/30">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="h-4 w-4 text-emerald-600" />
                <span className="text-xs font-medium text-muted-foreground">Regular</span>
              </div>
              <p className="text-lg font-bold text-emerald-600">€{totalCosts.regular.toFixed(2)}</p>
            </div>
            
            <div className="bg-white/80 dark:bg-background/80 rounded-lg p-3 border border-indigo-200/50 dark:border-indigo-800/30">
              <div className="flex items-center gap-2 mb-1">
                <Moon className="h-4 w-4 text-indigo-600" />
                <span className="text-xs font-medium text-muted-foreground">Night</span>
              </div>
              <p className="text-lg font-bold text-indigo-600">€{totalCosts.night.toFixed(2)}</p>
            </div>
            
            <div className="bg-white/80 dark:bg-background/80 rounded-lg p-3 border border-orange-200/50 dark:border-orange-800/30">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="h-4 w-4 text-orange-600" />
                <span className="text-xs font-medium text-muted-foreground">Holiday</span>
              </div>
              <p className="text-lg font-bold text-orange-600">€{totalCosts.holiday.toFixed(2)}</p>
            </div>
            
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-3 border-2 border-primary/30">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-primary" />
                <span className="text-xs font-medium text-muted-foreground">Total</span>
              </div>
              <p className="text-lg font-bold text-primary">€{totalCosts.total.toFixed(2)}</p>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-0">
          <ScrollArea className="w-full h-[400px]">
            <div className="p-6">
              <div className={cn("min-w-[700px] max-w-full", isMobile ? "pr-4" : "")}>
                <div className="rounded-lg border border-border/50 bg-gradient-to-r from-background to-muted/10 overflow-hidden shadow-sm">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gradient-to-r from-muted/30 to-muted/20 hover:from-muted/40 hover:to-muted/30 transition-all duration-200">
                        <TableHead className="w-[150px] font-semibold text-foreground py-4 px-6">
                          <div className="flex items-center gap-2">
                            <Euro className="h-4 w-4 text-emerald-600" />
                            {t("guard") || "Guard"}
                          </div>
                        </TableHead>
                        <TableHead className="text-center font-semibold text-foreground py-4">
                          <div className="flex flex-col items-center gap-1">
                            <Clock className="h-4 w-4 text-emerald-600" />
                            <span className="text-xs">{t("regularCost") || "Regular Cost"}</span>
                          </div>
                        </TableHead>
                        <TableHead className="text-center font-semibold text-foreground py-4">
                          <div className="flex flex-col items-center gap-1">
                            <Moon className="h-4 w-4 text-indigo-500" />
                            <span className="text-xs">{t("nightCost") || "Night Cost"}</span>
                          </div>
                        </TableHead>
                        <TableHead className="text-center font-semibold text-foreground py-4">
                          <div className="flex flex-col items-center gap-1">
                            <Calendar className="h-4 w-4 text-orange-500" />
                            <span className="text-xs">{t("holidayCost") || "Holiday Cost"}</span>
                          </div>
                        </TableHead>
                        <TableHead className="text-center font-semibold text-foreground py-4">
                          <div className="flex flex-col items-center gap-1">
                            <TrendingUp className="h-4 w-4 text-primary" />
                            <span className="text-xs">{t("totalCost") || "Total Cost"}</span>
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {guardsData.map((guard, index) => (
                        <GuardCostRow
                          key={guard.guardId}
                          guard={guard}
                          index={index}
                          onComputed={onRowComputed}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default GuardCostsTable;
